import { Box, Chip, CircularProgress } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import TeacherPayoutReports from 'src/mentor/components/TeacherPayoutReports/TeacherPayoutReports'
import { EVENT_TYPE_SCREENING } from 'src/consumer/constants'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { getMinimalMentorData } from 'src/supportPortal/views/therapists/util'

/**
 * Screener payout reports — the screener counterpart of TherapistPayoutReports. Reuses the shared
 * TeacherPayoutReports core, but locks the Service type to screening (defaultEventType +
 * allowedEventTypeIds) so it only ever shows screening-event payout data.
 */
export default function ScreenerPayoutReports() {
  const { mentorSlug } = useParams()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [currentScreener, setCurrentScreener] = useState()
  const [associatedClinics, setAssociatedClinics] = useState([])
  const [allEventTypes, setAllEventTypes] = useState([])
  const [paymentConfig, setPaymentConfig] = useState(null)
  const [paymentConfigLoaded, setPaymentConfigLoaded] = useState(false)

  const scopedClinicParams = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      clinicBrandId: params.get('clinicBrandId') || params.get('clinic-brand-id') || '',
      clinicLocationId: params.get('clinicLocationId') || params.get('clinic-location-id') || '',
    }
  }, [location.search])
  const fixedScopeParams = useMemo(
    () => ({
      ...(scopedClinicParams?.clinicBrandId
        ? { 'clinic-brand-id': scopedClinicParams.clinicBrandId }
        : {}),
      ...(scopedClinicParams?.clinicLocationId
        ? { 'clinic-location-id': scopedClinicParams.clinicLocationId }
        : {}),
    }),
    [scopedClinicParams?.clinicBrandId, scopedClinicParams?.clinicLocationId],
  )
  const practitionerContact = useMemo(() => {
    const email =
      currentScreener?.email ||
      currentScreener?.communicationPref?.email?.uid ||
      currentScreener?.contactEmail ||
      ''
    const mobile =
      currentScreener?.communicationPref?.whatsapp?.uid ||
      currentScreener?.mobile ||
      currentScreener?.contactMobile ||
      ''
    return {
      name: currentScreener?.name || currentScreener?.firstName || 'Screener',
      email,
      mobile,
    }
  }, [currentScreener])

  async function getSelectedScreener() {
    const resp = await getMinimalMentorData(
      mentorSlug,
      true,
      '/api/secure/cc/support/screeners/by-slug',
    )
    setCurrentScreener(resp)
    return resp
  }

  async function getPractitionerPaymentConfig(mentorId) {
    setPaymentConfigLoaded(false)
    if (!mentorId) {
      setPaymentConfig(null)
      setPaymentConfigLoaded(true)
      return
    }
    try {
      const response = await getSPDataWithParams(
        '/api/secure/cc/support/invoices/payment-config/get',
        { entityId: mentorId, entityType: 'mentor' },
      )
      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        setPaymentConfig(getDataMapFromCommonResponse(response)?.['payment-config'] || null)
      } else {
        setPaymentConfig(null)
      }
    } catch {
      setPaymentConfig(null)
    } finally {
      setPaymentConfigLoaded(true)
    }
  }

  async function getAllEventTypes() {
    const resp = await getSPDataWithParams('/api/secure/cc/support/reference/event-types', {})
    if (resp.status === 200) {
      setAllEventTypes(resp?.data?.sort((a, b) => a?.title?.localeCompare(b?.title)))
    }
  }

  async function getAssociatedBrands() {
    const resp = await getSPDataWithParams(
      '/api/secure/cc/support/screener/clinic-associations',
      { 'mentor-slug': mentorSlug },
      undefined,
      undefined,
      true,
    )
    if (resp && isCommonResponseSuccessfulV2(resp)) {
      setAssociatedClinics(getDataMapFromCommonResponse(resp)?.clinic)
    }
  }

  async function setup() {
    setLoading(true)
    const [screener] = await Promise.all([
      getSelectedScreener(),
      getAssociatedBrands(),
      getAllEventTypes(),
    ])
    await getPractitionerPaymentConfig(screener?.id)
    setLoading(false)
  }

  useEffect(() => {
    setup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="d-flex flex-column h-100">
      <SupportHeader heading={`${currentScreener?.firstName || 'Screener'} Payout Reports`} />
      {(scopedClinicParams?.clinicBrandId || scopedClinicParams?.clinicLocationId) && (
        <Box sx={{ px: 3, pt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {scopedClinicParams?.clinicBrandId && (
            <Chip label="Scoped to clinic brand" color="primary" variant="outlined" />
          )}
          {scopedClinicParams?.clinicLocationId && (
            <Chip label="Scoped to clinic location" color="secondary" variant="outlined" />
          )}
        </Box>
      )}
      {currentScreener && paymentConfigLoaded && !loading ? (
        <TeacherPayoutReports
          reportListApiSignature={'/api/secure/cc/support/payout-reports/mentor/paginated'}
          summaryReportApiSignature={'/api/secure/cc/support/payout-reports/mentor/summary'}
          allEventTypes={allEventTypes}
          defaultEventType={EVENT_TYPE_SCREENING}
          allowedEventTypeIds={[EVENT_TYPE_SCREENING]}
          paymentConfig={paymentConfig}
          onPaymentConfigSaved={(savedConfig) => setPaymentConfig(savedConfig || null)}
          reportsListingApiParams={{ 'teacher-id': currentScreener?.id, ...fixedScopeParams }}
          summaryReportApiParams={{ 'teacher-id': currentScreener?.id, ...fixedScopeParams }}
          bookedEventLogApiUrl="/api/secure/cc/support/events/logs/get"
          hideBackButton
          showSummaryReport
          useLocalDateFormatForApiCalls
          clinicAssociationData={associatedClinics}
          showAdminActions
          practitionerContact={practitionerContact}
        />
      ) : (
        <div className="d-flex justify-content-center align-items-center h-100 w-100">
          <CircularProgress />
        </div>
      )}
    </div>
  )
}
